import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModal from "../common/DeleteModal";
import config from "../../config";
import { ImageUploadIcon } from "./icons";
import { useTranslation } from "react-i18next";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#E8EAEE",
  borderRadius: "10px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  position: "relative",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    height: "55px",
    "& fieldset": {
      borderColor: "#183059",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "#183059",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#183059",
    },
    "&.Mui-error fieldset": {
      borderColor: "#d32f2f",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 24px",
  },
  "& .MuiFormHelperText-root": {
    marginLeft: "4px",
    marginTop: "4px",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  height: "55px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
    borderWidth: "2px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
  },
  "& .MuiSelect-select": {
    padding: "16px 24px",
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "12px",
  marginLeft: "4px",
  marginTop: "4px",
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F9E719",
  color: "#1F242E",
  height: "55px",
  border: "1px solid #414958",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "20px",
  "&:hover": {
    backgroundColor: "#FBC02D",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    color: "#666",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F2F2F3",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  border: "1px solid #414958",
  color: "#1F242E",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#F2F2F3",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  color: "#647490",
}));

const ImageUploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #183059",
  width: "220px",
  aspectRatio: "1",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#e8e8e8",
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  color: "#cc0000",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(204, 0, 0, 0.05)",
  },
}));

const REQUIRED_ASTERISK_SX = {
  color: "#d32f2f",
  ml: 0.5,
};

const sanitizeAlphaSpace = (value) => value.replace(/[^a-zA-Z\s]/g, "");
const sanitizeAlphaNumericId = (value) => value.replace(/[^a-zA-Z0-9-_]/g, "");
const sanitizePhoneNumber = (value) => value.replace(/\D/g, "").slice(0, 10);

export default function CreateAttendantForm({
  onClose,
  onSubmit,
  onDelete,
  initialData = null,
  isEdit = false,
}) {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [routeData, setRouteData] = useState([]);
  const [vehicleData, setVehicleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [formData, setFormData] = useState({
    fullName: (() => {
      const raw =
        initialData?.firstName && initialData?.lastName
          ? `${initialData.firstName} ${initialData.lastName}`
          : "";
      return raw.trim().endsWith(" .") ? raw.trim().slice(0, -2).trim() : raw;
    })(),
    staffId: initialData?.staffCode || "",
    route:
      initialData?.vehicle?.routeId ||
      initialData?.vehicle?.route?.id ||
      initialData?.routeId ||
      initialData?.route?.id ||
      "",
    phoneNumber: initialData?.phoneNumber || "",
    gender: initialData?.gender || "",
    assignedVehicle: initialData?.vehicleId || initialData?.vehicle?.id || "",
    profileImage: null,
  });

  const [errors, setErrors] = useState({
    fullName: "",
    staffId: "",
    route: "",
    phoneNumber: "",
    gender: "",
    assignedVehicle: "",
  });

  const [touched, setTouched] = useState({
    fullName: false,
    staffId: false,
    route: false,
    phoneNumber: false,
    gender: false,
    assignedVehicle: false,
  });

  const [imagePreview, setImagePreview] = useState(() => {
    // ✅ Get profile picture from initialData
    if (initialData?.documents) {
      const profilePicDoc = initialData.documents.find(
        (doc) => doc.name === "AttendantImage",
      );
      if (profilePicDoc) {
        return `${config.baseURL}/upload/image?fileName=${profilePicDoc.location}`;
      }
    }
    return null;
  });

  // Get organization ID and user ID from localStorage
  useEffect(() => {
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        const orgId = userState.user?.organizationId;
        const currentUserId = userState.user?.id;

        if (orgId) {
          setOrganizationId(orgId);
          setUserId(currentUserId);
        }
      }
    } catch (err) {
      console.error("Error reading user state:", err);
      setSubmitError("Failed to load user information");
    }
  }, []);

  // Fetch routes and vehicles when organizationId is available
  useEffect(() => {
    if (!organizationId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch routes
        const routesResponse = await axios.get(
          `${config.baseURL}/route/organization?organizationId=${organizationId}`,
          { withCredentials: true },
        );

        if (routesResponse.data) {
          setRouteData(
            Array.isArray(routesResponse.data) ? routesResponse.data : [],
          );
        }

        // Fetch vehicles
        const vehiclesResponse = await axios.get(
          `${config.baseURL}/vehicle/organization`,
          {
            params: { organizationId },
            withCredentials: true,
          },
        );

        if (vehiclesResponse.data) {
          const vehicles = vehiclesResponse.data || [];
          setVehicleData(
            vehicles.map((v) => ({
              id: v.id,
              name: v.name || v.VehicleNumber,
              vehicleNumber: v.VehicleNumber,
              routeId: v.routeId ?? v.route?.id ?? "",
            })),
          );
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setSubmitError("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  const filteredVehicleData = vehicleData;

  const validateField = (field, value) => {
    switch (field) {
      case "fullName":
        if (!value.trim()) {
          return t("Full name is required");
        }
        if (value.trim().length < 2) {
          return t("Full name must be at least 2 characters");
        }
        if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
          return t("Full name can contain only letters, numbers, and spaces");
        }
        return "";

      case "staffId":
        if (!value.trim()) {
          return t("Staff ID is required");
        }
        if (value.trim().length < 3) {
          return t("Staff ID must be at least 3 characters");
        }
        if (!/^[a-zA-Z0-9-_]+$/.test(value.trim())) {
          return t(
            "Staff ID can contain only letters, numbers, hyphen, and underscore",
          );
        }
        return "";

      case "phoneNumber":
        if (!value.trim()) {
          return t("Phone number is required");
        }
        if (!/^\d{10}$/.test(value.replace(/\s/g, ""))) {
          return t("Phone number must be 10 digits");
        }
        return "";

      case "gender":
        if (!value) {
          return t("Gender is required");
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const selectedVehicle =
      field === "assignedVehicle"
        ? vehicleData.find((vehicle) => String(vehicle.id) === String(value))
        : null;
    const updatedFormData =
      field === "assignedVehicle"
        ? {
            ...formData,
            assignedVehicle: value,
            route: selectedVehicle?.routeId || selectedVehicle?.route?.id || "",
          }
        : field === "route"
          ? formData
          : { ...formData, [field]: value };

    setSubmitError("");
    setFormData(updatedFormData);

    const error = validateField(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleBlur = (field) => () => {
    setTouched({ ...touched, [field]: true });
    const error = validateField(field, formData[field]);
    setErrors({ ...errors, [field]: error });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    if (onDelete) {
      try {
        await onDelete();
      } catch (error) {
        setSubmitError(t("Failed to delete attendant"));
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleImageUpload = (event) => {
    setImageError("");
    setSubmitError("");
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setImageError(
        t("Please upload a valid image file (JPEG, PNG, GIF, or WEBP)"),
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError(t("Image size should be less than 5MB"));
      return;
    }

    setFormData({
      ...formData,
      profileImage: file,
    });
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadProfilePicture = async (attendantId, file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("image", file);

      const response = await axios.post(
        `${config.baseURL}/upload/image?name=${encodeURIComponent(
          "AttendantImage",
        )}&associateEntityId=${attendantId}&associatedWith=Employee&type=profile`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading attendant profile picture:", error);
      throw error;
    }
  };

  const handleProfileImageDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isEdit && initialData?.id && !(formData.profileImage instanceof File)) {
      try {
        setIsSubmitting(true);
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: "AttendantImage",
            associateEntityId: initialData.id,
            associatedWith: "Employee",
          },
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting profile image:", error);
        setImageError(
          `${t("Failed to delete image")}: ${error.response?.data?.error || error.message}`,
        );
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    setImageError("");
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, profileImage: null }));
  };

  const handleSubmit = async () => {
    setSubmitError("");
    // Mark all fields as touched
    const allTouched = {
      fullName: true,
      staffId: true,
      route: true,
      phoneNumber: true,
      gender: true,
      assignedVehicle: true,
    };
    setTouched(allTouched);

    // Validate all fields
    const newErrors = {
      fullName: validateField("fullName", formData.fullName),
      staffId: validateField("staffId", formData.staffId),
      route: "",
      phoneNumber: validateField("phoneNumber", formData.phoneNumber),
      gender: validateField("gender", formData.gender),
      assignedVehicle: "",
    };
    setErrors(newErrors);

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");

    if (hasErrors) {
      setSubmitError(t("Please fill all required fields correctly"));
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare attendant data with routeId and vehicleId
      const attendantData = {
        fullName: (() => {
          const parts = formData.fullName.trim().split(/\s+/);
          return parts.length === 1
            ? `${parts[0]} .`
            : formData.fullName.trim();
        })(),
        staffId: formData.staffId,
        phoneNumber: formData.phoneNumber,
        gender: formData.gender,
        routeId: formData.route ? Number(formData.route) : null,
        vehicleId: formData.assignedVehicle
          ? Number(formData.assignedVehicle)
          : null,
        organizationId: organizationId,
      };

      console.log("Sending attendant data:", attendantData);

      let attendantId;

      if (isEdit && initialData?.id) {
        // Update existing attendant
        const response = await axios.put(
          `${config.baseURL}/employee`,
          {
            id: initialData.id,
            ...attendantData,
          },
          { withCredentials: true },
        );
        attendantId = initialData.id;
      } else {
        // Create new attendant
        const response = await axios.post(
          `${config.baseURL}/employee`,
          attendantData,
          { withCredentials: true },
        );
        attendantId = response.data.employee.id;
      }

      // Upload profile image if selected
      if (formData.profileImage instanceof File) {
        try {
          await uploadProfilePicture(attendantId, formData.profileImage);
        } catch (imageError) {
          console.error("Profile picture upload failed:", imageError);
          setImageError(t("Attendant saved but profile picture upload failed"));
        }
      }

      if (onSubmit) {
        onSubmit({ ...formData, id: attendantId });
      }

      // Reset form if creating new attendant
      if (!isEdit) {
        setFormData({
          fullName: "",
          staffId: "",
          route: "",
          phoneNumber: "",
          gender: "",
          assignedVehicle: "",
          profileImage: null,
        });
        setImagePreview(null);
        setImageError("");
        setErrors({
          fullName: "",
          staffId: "",
          route: "",
          phoneNumber: "",
          gender: "",
          assignedVehicle: "",
        });
        setTouched({
          fullName: false,
          staffId: false,
          route: false,
          phoneNumber: false,
          gender: false,
          assignedVehicle: false,
        });
      }

      // Close form after a delay
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitError(
        error.response?.data?.error || t("Failed to submit attendant data"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <Grid container spacing={3}>
        {/* Column 1: Image Upload (3 columns) */}
        <Grid item xs={12} md={3}>
          <input
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            style={{ display: "none" }}
            id={`image-upload-${isEdit ? "edit" : "create"}`}
            type="file"
            onChange={handleImageUpload}
          />

          <Box sx={{ position: "relative", width: "220px" }}>
            <label htmlFor={`image-upload-${isEdit ? "edit" : "create"}`}>
              <ImageUploadBox>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <ImageUploadIcon />
                )}
              </ImageUploadBox>
            </label>

            {/* Icons sit on the top-right border of the circle */}
            {imagePreview && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 4,
                  zIndex: 2,
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 1,
                }}
              >
                {/* Edit Icon */}
                <label
                  htmlFor={`image-upload-${isEdit ? "edit" : "create"}`}
                  style={{ cursor: "pointer" }}
                >
                  <svg
                    width="52"
                    height="36"
                    viewBox="0 0 52 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      fill="#F2F2F3"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      stroke="#20A8E0"
                    />
                    <path
                      d="M29.333 9.67676L31.833 12.1768L29.9272 14.0834L27.4272 11.5834L29.333 9.67676ZM19.333 19.6668V22.1668H21.833L28.7488 15.2609L26.2488 12.7609L19.333 19.6668ZM19.333 24.6668H32.6663V26.3334H19.333V24.6668Z"
                      fill="#20A8E0"
                    />
                  </svg>
                </label>

                {/* Delete Icon */}
                <Box
                  component="span"
                  onClick={handleProfileImageDelete}
                  sx={{ cursor: "pointer" }}
                >
                  <svg
                    width="52"
                    height="36"
                    viewBox="0 0 52 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      fill="#F2F2F3"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      stroke="#EE3B2B"
                    />
                    <path
                      d="M29.3327 11H26.666L25.3327 9.66699H20.666L19.3327 11H16.666V13.667H29.3327V11ZM17.9993 25.667C17.9993 27.1337 19.1993 28.3337 20.666 28.3337H25.9993C27.466 28.3337 28.666 27.1337 28.666 25.667V14.3337H17.9993V25.667ZM20.666 17.0003H25.9993V25.667H20.666V17.0003Z"
                      fill="#EE3B2B"
                    />
                  </svg>
                </Box>
              </Box>
            )}

            {imageError && (
              <ErrorText sx={{ textAlign: "center", mt: 1 }}>
                {imageError}
              </ErrorText>
            )}
          </Box>
        </Grid>
        {/* Column 2: Full Name, Staff ID, Route (4.5 columns) */}
        <Grid item xs={12} md={4.5}>
          <Grid container spacing={2}>
            {/* Row 1: Full Name and Staff ID */}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Full Name")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.fullName}
                onChange={handleChange("fullName")}
                onBlur={handleBlur("fullName")}
                placeholder={t("Enter full name")}
                error={touched.fullName && !!errors.fullName}
                helperText={touched.fullName && errors.fullName}
                disabled={loading || isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Staff ID")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.staffId}
                onChange={handleChange("staffId")}
                onBlur={handleBlur("staffId")}
                placeholder={t("Enter staff ID")}
                error={touched.staffId && !!errors.staffId}
                helperText={touched.staffId && errors.staffId}
              />
            </Grid>

            {/* Row 2: Route */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Route")}
              </Typography>
              <FormControl fullWidth error={touched.route && !!errors.route}>
                <StyledSelect
                  value={formData.route}
                  displayEmpty
                  disabled
                >
                  <MenuItem value="">
                    {t("Follows the assigned vehicle")}
                  </MenuItem>
                  {routeData.map((route) => (
                    <MenuItem key={route.id} value={route.id}>
                      {route.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
                {touched.route && errors.route && (
                  <ErrorText>{errors.route}</ErrorText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {/* Column 3: Phone Number, Gender, Assigned Vehicle (4.5 columns) */}
        <Grid item xs={12} md={4.5}>
          <Grid container spacing={2}>
            {/* Row 1: Phone Number and Gender */}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Phone Number")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.phoneNumber}
                onChange={handleChange("phoneNumber")}
                onBlur={handleBlur("phoneNumber")}
                placeholder={t("Enter phone no.")}
                error={touched.phoneNumber && !!errors.phoneNumber}
                helperText={touched.phoneNumber && errors.phoneNumber}
                disabled={loading || isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Gender")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <FormControl fullWidth error={touched.gender && !!errors.gender}>
                <StyledSelect
                  value={formData.gender}
                  onChange={handleChange("gender")}
                  onBlur={handleBlur("gender")}
                  displayEmpty
                  error={touched.gender && !!errors.gender}
                  disabled={loading || isSubmitting}
                >
                  <MenuItem value="" disabled>
                    {t("Select gender")}
                  </MenuItem>
                  <MenuItem value="MALE">{t("Male")}</MenuItem>
                  <MenuItem value="FEMALE">{t("Female")}</MenuItem>
                  <MenuItem value="OTHER">{t("Other")}</MenuItem>
                </StyledSelect>
                {touched.gender && errors.gender && (
                  <ErrorText>{errors.gender}</ErrorText>
                )}
              </FormControl>
            </Grid>

            {/* Row 2: Assigned Vehicle */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Assigned Vehicle")}
              </Typography>
              <FormControl
                fullWidth
                error={touched.assignedVehicle && !!errors.assignedVehicle}
              >
                <StyledSelect
                  value={formData.assignedVehicle}
                  onChange={handleChange("assignedVehicle")}
                  onBlur={handleBlur("assignedVehicle")}
                  displayEmpty
                  error={touched.assignedVehicle && !!errors.assignedVehicle}
                  disabled={loading || isSubmitting}
                >
                  <MenuItem value="" disabled>
                    {t("Select Vehicle")}
                  </MenuItem>
                  {filteredVehicleData.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name} - {vehicle.vehicleNumber}
                    </MenuItem>
                  ))}
                </StyledSelect>
                {touched.assignedVehicle && errors.assignedVehicle && (
                  <ErrorText>{errors.assignedVehicle}</ErrorText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      {submitError && (
        <ErrorText sx={{ textAlign: "center", mt: 3 }}>{submitError}</ErrorText>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={submitError ? 1 : 3}
      >
        <Box>
          {isEdit && onDelete && (
            <DeleteButton
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
              disabled={isSubmitting}
            >
              {t("Delete")}
            </DeleteButton>
          )}
        </Box>
        <Box display="flex" gap={8}>
          {!isEdit && (
            <CancelButton onClick={onClose} disabled={isSubmitting}>
              {t("Cancel")}
            </CancelButton>
          )}
          <ConfirmButton
            onClick={handleSubmit}
            disabled={isSubmitting || loading}
          >
            {isEdit ? t("Update") : t("Confirm")}
          </ConfirmButton>
        </Box>
        <Box></Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Attendant!")}
        message={t(
          "Are you sure you want to delete this attendant? This action cannot be undone.",
        )}
        cancelButtonText={t("Cancel")}
        confirmButtonText={t("Delete")}
      />
    </FormContainer>
  );
}
